"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-base font-medium transition-all hover:text-primary hover:underline underline-offset-8 decoration-primary/50 decoration-2 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} onClick={onItemClick} className="block rounded-md px-4 py-3 text-lg font-medium transition-colors hover:bg-accent hover:text-primary">
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <img src="https://unified-warthog.10web.cloud/wp-content/uploads/2026/03/sloth-look-left-no-bg-w-Kanha-ERP.png" alt="Kanha ERP Logo" className="h-9 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 items-center justify-center px-8">
            <MenuProvider menu_id="46">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
            <div className="flex flex-col items-end justify-center">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
                Client Support
              </span>
              <Link to="tel:+1-720-772-6420" className="flex items-center gap-1.5 text-primary font-semibold text-base hover:underline transition-all">
                <Phone className="size-4" />
                +1-720-772-6420
              </Link>
            </div>
            <Link to="#consultation-portal">
              <Button className="rounded-[4px] px-6 h-11 font-medium shadow-sm">
                Contact Us
              </Button>
            </Link>
          </div>

          {}
          <div className="flex lg:hidden items-center gap-4">
            <Link to="tel:+1-720-772-6420" className="flex items-center justify-center size-10 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors" aria-label="Call Support">
              <Phone className="size-5" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" className="p-2 -mr-2">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-background border-t border-border overflow-y-auto z-40 animate-in slide-in-from-top-2 duration-200">
          <div className="flex flex-col min-h-full pb-8">
            <MenuProvider menu_id="46">
              <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="mt-auto p-6 border-t border-border bg-muted/30">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    Client Support
                  </span>
                  <Link to="tel:+1-720-772-6420" className="flex items-center gap-2 text-primary font-semibold text-xl">
                    <Phone className="size-5" />
                    +1-720-772-6420
                  </Link>
                </div>
                <Link to="#consultation-portal">
                  <Button className="w-full rounded-[4px] h-12 text-base mt-2">
                    Contact Us
                  </Button>
                 </Link>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}